import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section } from "../../components/vc/layout";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { ImageViewer } from "../../components/vc/ImageViewer";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  ArrowLeft, ClipboardList, Sparkles, Building2, Search, ChevronDown, X,
  CheckCircle2, Flag, Calendar, Users, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_OPTIONS = [
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-600" },
  { v: "medium", l: "Medium", cls: "bg-sky-100 text-sky-700" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-700" },
  { v: "urgent", l: "Urgent", cls: "bg-red-100 text-red-700" },
];

/**
 * Assign Order to Employee — reached IMMEDIATELY after Place Order in the new outgoing workflow.
 * Mirrors the Assign Task pattern: shows uploaded items/notes/images per order + employee picker + priority + due date.
 * On assign, creates one task per order (all linked to their own order_id and pointing to the same employee).
 * On successful assignment, redirects to /employees/:id (Pending Tasks) — same as Assign Task.
 * "Skip" button lets the user jump directly to Orders dashboard if they don't want to assign right now.
 */
export default function VCAssignOrder() {
  const location = useLocation();
  const navigate = useNavigate();
  const passed = location.state || {};
  const orders = Array.isArray(passed.orders) ? passed.orders : [];

  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [empOpen, setEmpOpen] = useState(false);
  const [empQuery, setEmpQuery] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  // If the user reached this page directly (no orders in state), send them to Orders
  useEffect(() => {
    if (!orders || orders.length === 0) {
      navigate("/orders", { replace: true });
    }
  }, [orders, navigate]);

  const grandTotal = useMemo(
    () => orders.reduce((s, o) => s + Number(o.price || 0), 0),
    [orders]
  );

  const selectedEmp = useMemo(
    () => employees.find((e) => e.id === employeeId) || null,
    [employees, employeeId]
  );

  const filteredEmps = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.role || "").toLowerCase().includes(q) ||
      (e.phone || "").toLowerCase().includes(q)
    );
  }, [employees, empQuery]);

  const doAssign = async () => {
    if (!employeeId) { toast.error("Choose an employee to assign"); return; }
    if (orders.length === 0) { navigate("/orders"); return; }
    setSubmitting(true);
    try {
      // Atomic bulk create — server-side rollback on partial failure prevents duplicates on retry.
      const tasks = orders.map((o) => {
        const linesCount = (o.lines && o.lines.length) || 1;
        const title = linesCount > 1
          ? `Fulfill order · ${linesCount} items → ${o.supplier_name}`
          : `Fulfill: ${o.product} → ${o.supplier_name}`;
        return {
          title,
          description: (o.notes || "").trim(),
          order_id: o.id,
          employee_id: employeeId,
          priority,
          deadline: dueDate || null,
        };
      });
      await apiClient.post("/tasks/bulk", { tasks });
      // Note: order-level assigned-employee visibility for the customer is
      // now owned by the SUPPLIER (see IncomingOrderDetail's assign dialog).
      // Do NOT write assigned_employee_* fields from the buyer's post-place
      // flow — that would leak the buyer's internal team member onto both
      // parties' Order Details.
      toast.success(`Assigned ${orders.length} order${orders.length === 1 ? "" : "s"} to ${selectedEmp?.name || "employee"}`);
      navigate(`/employees/${employeeId}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to assign");
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => navigate("/orders");

  if (!orders || orders.length === 0) return null; // being redirected

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-5">
        <button
          onClick={() => navigate("/orders")}
          data-testid="assign-order-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Orders
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3 h-3" strokeWidth={2.6} /> Order Placed
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Assign to Employee</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Delegate fulfilment for {orders.length} order{orders.length === 1 ? "" : "s"} · Total ₹{grandTotal.toFixed(2)}
          </p>
        </div>

        {/* Order previews (mirrors Assign Task's brief/preview style) */}
        <Section title="Placed orders" subtitle="Review before assigning" testid="assign-order-preview">
          <div className="space-y-2.5" data-testid="assign-order-list">
            {orders.map((o, oi) => (
              <div
                key={o.id || oi}
                data-testid={`placed-order-${o.id || oi}`}
                className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3"
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                    <Building2 className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Supplier</div>
                    <div className="font-bold text-slate-900 text-sm leading-tight truncate">{o.supplier_name || "Supplier"}</div>
                    {o.id && (
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        #{(o.id || "").slice(0, 6).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <StatusBadge status="pending" testid={`placed-order-status-${o.id || oi}`} />
                </div>

                {/* Items */}
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  {(o.lines && o.lines.length > 0 ? o.lines : [{
                    product: o.product,
                    quantity: o.quantity,
                    unit: o.unit,
                    unit_price: o.unit_price,
                    price: o.price,
                  }]).map((ln, i) => {
                    const qty = Number(ln.quantity || 0);
                    const unitPrice = Number(ln.unit_price != null ? ln.unit_price : (qty ? Number(ln.price || 0) / qty : 0));
                    const lineTotal = Number(ln.price != null ? ln.price : unitPrice * qty);
                    return (
                      <div key={i} className="px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-900 truncate flex-1">{ln.product}</div>
                          <div data-testid={`placed-line-total-${o.id || oi}-${i}`} className="text-sm font-bold text-slate-900 shrink-0">
                            ₹{lineTotal.toFixed(2)}
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          ₹{unitPrice.toFixed(2)} × {qty} {ln.unit || ""} = ₹{lineTotal.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between px-3 py-2 bg-emerald-50">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Order total</span>
                    <span data-testid={`placed-order-total-${o.id || oi}`} className="text-sm font-bold text-emerald-900">
                      ₹{Number(o.price || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {o.notes && (
                  <div data-testid={`placed-order-notes-${o.id || oi}`} className="bg-amber-50 border border-amber-100 rounded-xl p-2.5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-0.5">Notes</div>
                    <div className="text-xs text-amber-900 whitespace-pre-wrap">{o.notes}</div>
                  </div>
                )}

                {o.image_data && (
                  <ImageViewer src={o.image_data} label="Attached image" testid={`placed-order-image-${o.id || oi}`} />
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Employee picker (searchable) + priority + due date */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3 shadow-sm">
          <div className="flex items-center gap-1.5 text-slate-900 font-bold text-sm">
            <ClipboardList className="w-4 h-4 text-emerald-600" /> Task details
          </div>

          {/* Employee */}
          <div className="space-y-1.5">
            <Label>Assign to employee *</Label>
            {employees.length === 0 ? (
              <SecondaryButton onClick={() => navigate("/employees")} data-testid="assign-order-add-emp" className="w-full">
                Add an employee first
              </SecondaryButton>
            ) : (
              <Popover open={empOpen} onOpenChange={setEmpOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="assign-order-emp-trigger"
                    className="w-full flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-2xl h-12 px-3.5 text-sm font-semibold text-slate-700 hover:border-emerald-300 transition"
                  >
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="truncate">
                        {selectedEmp ? `${selectedEmp.name}${selectedEmp.role ? ` · ${selectedEmp.role}` : ""}` : "Choose employee..."}
                      </span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition ${empOpen ? "rotate-180" : ""}`} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={6}
                  className="w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl overflow-hidden"
                  data-testid="assign-order-emp-popover"
                >
                  <div className="flex items-center gap-2 border-b border-slate-100 px-3 h-11">
                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                      autoFocus
                      value={empQuery}
                      onChange={(e) => setEmpQuery(e.target.value)}
                      placeholder="Search employees..."
                      data-testid="assign-order-emp-search"
                      className="w-full h-full outline-none text-sm bg-transparent placeholder:text-slate-400"
                    />
                    {empQuery && (
                      <button onClick={() => setEmpQuery("")} className="text-slate-400 hover:text-slate-600">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1" data-testid="assign-order-emp-options">
                    {filteredEmps.length === 0 ? (
                      <div className="text-xs text-slate-500 text-center py-6">No employees match</div>
                    ) : (
                      filteredEmps.map((e) => {
                        const on = employeeId === e.id;
                        return (
                          <button
                            key={e.id}
                            onClick={() => { setEmployeeId(e.id); setEmpOpen(false); }}
                            data-testid={`assign-order-emp-option-${e.id}`}
                            data-selected={on ? "true" : "false"}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-emerald-50 transition ${on ? "bg-emerald-50" : ""}`}
                          >
                            <div className={`w-5 h-5 rounded-full grid place-items-center shrink-0 text-[10px] font-bold ${on ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                              {(e.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-slate-900 truncate">{e.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">{e.role || "Staff"}{e.phone ? ` · ${e.phone}` : ""}</div>
                            </div>
                            {on && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={2.6} />}
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Priority</Label>
            <div className="grid grid-cols-4 gap-1.5" data-testid="assign-order-priority">
              {PRIORITY_OPTIONS.map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPriority(p.v)}
                  data-testid={`assign-order-priority-${p.v}`}
                  className={`h-10 rounded-xl text-xs font-bold transition ${
                    priority === p.v ? p.cls + " ring-2 ring-offset-1 ring-slate-900/10" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>

          {/* Due date */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Due date</Label>
            <Input
              data-testid="assign-order-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="h-12 rounded-xl text-base"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <PrimaryButton
            onClick={doAssign}
            loading={submitting}
            data-testid="assign-order-submit"
            className="w-full h-14 flex items-center justify-center gap-2 text-base"
          >
            <CheckCircle2 className="w-5 h-5" />
            {orders.length > 1 ? `Assign ${orders.length} Orders` : "Assign Order to Employee"}
          </PrimaryButton>
          <SecondaryButton
            onClick={skip}
            data-testid="assign-order-skip"
            className="w-full h-12 flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" />
            Skip · View Orders
          </SecondaryButton>
        </div>
      </div>
    </>
  );
}
